/**
 * Multi-Add origin slice helpers — post-add "needs configuration"
 * notifications.
 *
 * Provides the load-bearing client-only primitive for the post-add
 * notification workflow:
 *
 *   1. `registerMultiAddOrigin` / `unregisterMultiAddOrigin` — `runBulkCommit`
 *      (and the transitory placeholder path) register committed devices so
 *      the observer can distinguish multi-added devices from Wizard /
 *      single-add ones; the auth-commit migration unregisters the placeholder.
 *   2. `isMultiAddOrigin` — the observer hook and the Wizard-negative-
 *      case test use this to gate notification fires.
 *   3. `getLastDesiredAlert` / `setLastDesiredAlert` / `clearLastDesiredAlert`
 *      — per-DEVICE last-posted desired-alert identity for the declarative
 *      reconciliation (ENG-4486). The observer re-posts only when a device's
 *      desired alert identity changes, which yields "exactly one alert per
 *      device", auto-clear on resolution, and dismissal-respect by
 *      construction. Replaces the old per-(device, bucket) sticky dedup.
 *   4. `markTabDismissed` / `isTabDismissed` — per-(device, sub-tab)
 *      Advanced Settings "needs attention" dismissal (Multi-Add Phase 2).
 *   5. `markSubtabAnalyticsShown` / `hasSubtabAnalyticsShown` —
 *      per-(device, sub-tab) analytics-emit dedup (Multi-Add Phase 2).
 *
 * Session-scoped: state lives in the in-memory zustand depot and resets on
 * app restart / depot recreate (`State.multiAddOriginByDeviceId` and
 * `State.lastDesiredAlertByDevice` both init to `{}`). NOT persisted, NOT
 * carried over state-sync. Verified by the "initial state is empty" test in
 * `__tests__/multi-add-origins.test.ts`.
 *
 * Per `think-in-systems` (ai.knowledge/global): minimal primitive —
 * helpers operate on opaque string bucket keys so the observer hook
 * owns the `ChipTargetTab` → bucket-string mapping rather than this
 * slice.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
import type { Device } from '@studio/models/device';
import type { MultiAddOrigin } from '#~/depot/types.js';
import { depot } from '#~/depot/index.js';
import { releaseConnectionHold } from '#~/room/components/video/sources/item/connection-failure-hold.js';

/**
 * Compose the dedup key for a (device, bucket) pair. Module-private —
 * callers use the typed helpers below, not the raw key.
 *
 * Per `stay-in-scope` (ai.knowledge/global): the `<deviceId>:<bucket>`
 * shape is the contract the hook and resolver depend on; changing it
 * requires updating both call sites in lockstep.
 */
const bucketKey = (deviceId: string, bucket: string): string => `${deviceId}:${bucket}`;

/**
 * Register devices as multi-add origins under a shared batch id.
 *
 * Called by `runBulkCommit` for each device that successfully commits.
 * Wizard / single-add path NEVER calls this — see the negative-case
 * test in `__tests__/multi-add-origins.test.ts`.
 *
 * Idempotent on duplicate `deviceId` within the same call OR across
 * calls: the latest registration overwrites the previous origin record
 * (last-write-wins). This is intentional — a re-add of the same device
 * inside a new batch should track the latest batchId so any new
 * notification fires reference the latest grouping. The dedup set
 * The per-device last-desired-alert tracking (`lastDesiredAlertByDevice`)
 * is NOT cleared by re-registration — the reconciliation reconverges it.
 *
 * @param deviceIds - Devices to register. Empty array is a no-op.
 * @param batchId - Parent batch id from `runBulkCommit`.
 */
export const registerMultiAddOrigin = (deviceIds: ReadonlyArray<string>, batchId: string): void => {
  // Skip the depot write on an empty input — both keeps tests honest
  // and avoids a no-op zustand notification under immer.
  if (deviceIds.length === 0) {
    return;
  }

  const { produce } = depot.getState();
  const createdAt = new Date();

  produce((draft) => {
    for (const deviceId of deviceIds) {
      const origin: MultiAddOrigin = { batchId, createdAt };
      draft.multiAddOriginByDeviceId[deviceId] = origin;
    }
  });
};

/**
 * Remove a device's multi-add origin entry (no-op when absent).
 *
 * The single caller is the transitory-origin migration (ENG-4486 Fix A.3,
 * `migrate-transitory-origin.ts`): once an ONVIF placeholder authenticates
 * and commits as a REAL device, the placeholder id's origin is migrated to
 * the real id, and the stale placeholder origin is dropped here so the
 * observer never iterates a device that no longer exists. Simple
 * produce-delete on the origin slice.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 *
 * @param deviceId - Device id whose origin entry to remove.
 */
export const unregisterMultiAddOrigin = (deviceId: string): void => {
  const { produce } = depot.getState();

  produce((draft) => {
    delete draft.multiAddOriginByDeviceId[deviceId];
  });
};

// Drop a device's connection-failure hold on Save/replace: a multi-add origin stays registered across a replace, so without this the stale FAILED verdict carries into the re-added device's fresh CONNECTING window.
export const releaseConnectionHoldForDevice = (deviceId: string): void => {
  const { produce } = depot.getState();

  produce((draft) => {
    releaseConnectionHold(draft, deviceId);
  });
};

/**
 * Returns `true` when `deviceId` was registered via
 * `registerMultiAddOrigin` (i.e. came from the multi-add pipeline).
 *
 * The observer hook uses this as the gate for firing post-add
 * notifications: Wizard / single-add devices return `false` here and
 * therefore never fire a notification.
 *
 * Non-reactive read — call from inside a `useDepot` selector when
 * reactive updates are needed.
 *
 * @param deviceId - Device id to check.
 * @returns `true` if registered as multi-add origin, `false` otherwise.
 */
export const isMultiAddOrigin = (deviceId: string): boolean => {
  const { multiAddOriginByDeviceId } = depot.getState();
  return multiAddOriginByDeviceId[deviceId] !== undefined;
};

/**
 * Read a device's LAST-POSTED desired-alert identity (the live alert's
 * toast id), or `undefined` when no alert is currently posted for it.
 *
 * The observer's declarative reconciliation (ENG-4486) compares this against
 * the freshly-computed DESIRED identity each tick and re-posts ONLY on a
 * change — which is what respects user dismissal (a dismissed toast is never
 * re-posted while its identity is unchanged) and makes "one alert per device"
 * true by construction.
 *
 * Non-reactive read — call from inside a `useDepot` selector when reactive
 * updates are needed.
 *
 * @param deviceId - Device id.
 * @returns The live alert's toast id, or `undefined`.
 */
export const getLastDesiredAlert = (deviceId: string): string | undefined => {
  const { lastDesiredAlertByDevice } = depot.getState();
  return lastDesiredAlertByDevice[deviceId];
};

/**
 * Record `identity` (a toast id) as the device's last-posted desired alert,
 * called by the observer AFTER it posts the alert. A subsequent tick whose
 * desired identity equals this is a no-op (dismissal-respecting).
 *
 * @param deviceId - Device id.
 * @param identity - The posted alert's toast id.
 */
export const setLastDesiredAlert = (deviceId: string, identity: string): void => {
  const { produce } = depot.getState();

  produce((draft) => {
    draft.lastDesiredAlertByDevice[deviceId] = identity;
  });
};

/**
 * Drop a device's last-desired-alert entry, called by the observer when the
 * device's desired alert becomes NONE (all settings resolved → auto-clear,
 * PRD §9.3) or when the device is no longer an origin.
 *
 * @param deviceId - Device id.
 */
export const clearLastDesiredAlert = (deviceId: string): void => {
  const { produce } = depot.getState();

  produce((draft) => {
    delete draft.lastDesiredAlertByDevice[deviceId];
  });
};

// --- Advanced Settings sub-tab "needs attention" helpers ---------------------
// Multi-Add Phase 2. Parallel to the bucket helpers above — same
// shape, different slice. The sub-tab warning system is a render-
// time concern (per-tab boolean derived by
// `deriveNeedAttentionTabs`), but the dismissal state and the
// analytics-emit dedup BOTH live on the depot so they survive across
// React unmounts within the same session. Per `think-in-systems`
// (ai.knowledge/global) — minimal coupling, shared accessor primitive.

/** Sub-tab keys that can be flagged as "needs attention". */
type NeedAttentionTab = 'authentication' | 'video' | 'control' | 'driver';

/**
 * Mark a `(deviceId, tab)` pair as dismissed. The render layer
 * passes the dismissal map into `deriveNeedAttentionTabs`, which
 * returns `false` for any dismissed tab regardless of underlying
 * status.
 *
 * One-way: there is no "un-dismiss" helper — the only way to clear
 * is depot recreate / app restart, by design (the user has
 * acknowledged the warning for this session).
 *
 * Per `multi-writer-registry-completeness`
 * (ai.knowledge/platform/studio.client): the depot's init populates
 * `dismissedNeedAttentionTabs` to `{}` so we can index `draft.
 * dismissedNeedAttentionTabs[deviceId] ?? {}` without a depot-init
 * race.
 *
 * @param deviceId - Device id.
 * @param tab - Sub-tab key (`authentication` / `video` / `control` /
 *   `driver`).
 */
export const markTabDismissed = (deviceId: string, tab: NeedAttentionTab): void => {
  const { produce } = depot.getState();
  // The depot slice is keyed by opaque `Device['id']`; the wider call
  // surface accepts raw string for ergonomic reasons (matches the sibling
  // origin helpers). Cast at the boundary.
  const key = deviceId as Device['id'];

  produce((draft) => {
    // Lazily allocate the per-device record so we don't carry empty
    // objects for devices that never dismiss anything.
    const existing = draft.dismissedNeedAttentionTabs[key] ?? {};
    existing[tab] = true;
    draft.dismissedNeedAttentionTabs[key] = existing;
  });
};

/**
 * Returns `true` when the user has previously dismissed `tab` on
 * `deviceId`. Used by the render layer as a guard before
 * lighting the warning icon.
 *
 * Non-reactive read — call from inside a `useDepot` selector when
 * reactive updates are needed.
 *
 * @param deviceId - Device id.
 * @param tab - Sub-tab key.
 * @returns `true` if dismissed, `false` otherwise.
 */
export const isTabDismissed = (deviceId: string, tab: NeedAttentionTab): boolean => {
  const { dismissedNeedAttentionTabs } = depot.getState();
  // Cast at the boundary — see `markTabDismissed` for rationale.
  return dismissedNeedAttentionTabs[deviceId as Device['id']]?.[tab] === true;
};

/**
 * Mark a `(deviceId, tab)` pair as having had the
 * `MULTI_ADD_SUBTAB_NEEDS_ATTENTION_SHOWN` analytics event already
 * emitted in this session. The `useNeedAttentionTabs` hook fires the event once
 * per `false → true` transition; this dedup set prevents re-emit on
 * React unmount/remount cycles within the same session.
 *
 * Sticky: a tab that resolves and then re-trips does NOT re-emit
 * within the same session. Per the engineer's locked analytics UX
 * choice (one emit per session per transition).
 *
 * @param deviceId - Device id.
 * @param tab - Sub-tab key.
 */
export const markSubtabAnalyticsShown = (deviceId: string, tab: NeedAttentionTab): void => {
  const { produce } = depot.getState();
  const key = bucketKey(deviceId, tab);

  produce((draft) => {
    draft.shownSubtabAnalytics[key] = true;
  });
};

/**
 * Returns `true` when `(deviceId, tab)` has already had the
 * `MULTI_ADD_SUBTAB_NEEDS_ATTENTION_SHOWN` event emitted.
 *
 * Non-reactive read.
 *
 * @param deviceId - Device id.
 * @param tab - Sub-tab key.
 * @returns `true` if already emitted, `false` otherwise.
 */
export const hasSubtabAnalyticsShown = (deviceId: string, tab: NeedAttentionTab): boolean => {
  const { shownSubtabAnalytics } = depot.getState();
  return shownSubtabAnalytics[bucketKey(deviceId, tab)] === true;
};
